import React, { useState, useEffect, useCallback } from "react";
import {
    Container,
    Segment,
    Header,
    Button,
    Table,
    Icon,
    Dropdown,
    Loader,
    Message,
} from "semantic-ui-react";
import ReactMarkdown from "react-markdown";
import { useHistory } from "react-router-dom";
import { GetUserActivity, UserActivityEntry } from "../Utils/UserAPI";

const DEFAULT_DAYS = 30;
const DAY_OPTIONS = [7, 14, 30, 60, 90, 180, 365];

export default function UserActivity() {
    const history = useHistory();
    const [days, setDays] = useState(DEFAULT_DAYS);
    const [activity, setActivity] = useState<UserActivityEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Convert legacy HTML bold tags to markdown format, same as the
    // per-user history modal in UserManagement.
    const convertLegacyText = (text: string): string => {
        return text
            .replace(/<b>/g, '**')
            .replace(/<\/b>/g, '**');
    };

    const loadActivity = useCallback((requestedDays: number) => {
        setLoading(true);
        setError(null);
        GetUserActivity(requestedDays, (statusCode, data) => {
            setLoading(false);
            if (statusCode === 200) {
                setActivity(data);
            } else if (statusCode === 403) {
                setError("You do not have permission to view user edit activity.");
            } else {
                setError("Failed to load user edit activity. Please try again.");
            }
        });
    }, []);

    useEffect(() => {
        loadActivity(days);
    }, [days, loadActivity]);

    return (
        <Container>
            <Header as="h2">
                <Icon name="history" />
                <Header.Content>
                    User Edit Activity
                    <Header.Subheader>
                        Admin-only feed of changes made to member records
                    </Header.Subheader>
                </Header.Content>
            </Header>

            <div style={{ marginBottom: "1rem" }}>
                <Button onClick={() => history.push("/users")}>
                    <Icon name="arrow left" />
                    Back to User List
                </Button>
            </div>

            <Segment>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
                    <label>Show changes from the last</label>
                    <Dropdown
                        selection
                        compact
                        options={DAY_OPTIONS.map((d) => ({ key: d, text: `${d} days`, value: d }))}
                        value={days}
                        onChange={(e, { value }) => setDays(value as number)}
                    />
                </div>

                {loading ? (
                    <Loader active size="large">
                        Loading activity...
                    </Loader>
                ) : error ? (
                    <Message negative>
                        <Message.Header>Error</Message.Header>
                        <p>{error}</p>
                    </Message>
                ) : activity.length === 0 ? (
                    <Message info>
                        <Message.Header>No Activity Found</Message.Header>
                        <p>No user edits have been recorded in the last {days} days.</p>
                    </Message>
                ) : (
                    <Table celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Date/Time</Table.HeaderCell>
                                <Table.HeaderCell>User Edited</Table.HeaderCell>
                                <Table.HeaderCell>Modified By</Table.HeaderCell>
                                <Table.HeaderCell>Changes</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {activity.map((entry) => (
                                <Table.Row key={entry.id}>
                                    <Table.Cell>
                                        {new Date(entry.time).toLocaleString()}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <a href={`/users/${entry.user_id}`}>
                                            {entry.user_first_name} {entry.user_last_name}
                                        </a>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {entry.modified_by_user_id === entry.user_id ? (
                                            "Self"
                                        ) : (
                                            <a href={`/users/${entry.modified_by_user_id}`}>
                                                {entry.modified_by_user_first_name} {entry.modified_by_user_last_name}
                                            </a>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div style={{ whiteSpace: "pre-line" }}>
                                            <ReactMarkdown>
                                                {convertLegacyText(entry.text)}
                                            </ReactMarkdown>
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                )}
            </Segment>
        </Container>
    );
}
